// --------------------------------------------------------------
// Created On: 2026-08-28
// Author: Dimitra Weinstein
//
// Last Modified: 2026-09-14
// Modified By: Dimitra Weinstein
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { useMemo } from "react";
import { SIGNAL_STRENGTH_ATTRIBUTE, RECENTLY_COMMUNICATED_ATTRIBUTE } from "../../constants/attributes";
import styles from "./SignalIcon.module.scss";

// Signal icon that is white or grey, depending on the props passed to it.
export default function SignalIcon(props: Props): Component {
  const signalStrength: string = useMemo(() => {
    return getDeviceSignalStrength(props.allAttributes);
  }, [JSON.stringify(props.allAttributes)]);

  // Determine a device's signal strength.
  function getDeviceSignalStrength(attributes: Attribute[]): string {
    const signalStrengthAttribute: Attribute | undefined = attributes.find(
      (attribute) => attribute.attributeCode === SIGNAL_STRENGTH_ATTRIBUTE
    );
    const recentlyCommunicatedAttribute: Attribute | undefined = attributes.find(
      (attribute) => attribute.attributeCode === RECENTLY_COMMUNICATED_ATTRIBUTE
    );

    if (
      recentlyCommunicatedAttribute === undefined ||
      recentlyCommunicatedAttribute.currentValue === null ||
      recentlyCommunicatedAttribute.currentValue === "False"
    ) {
      return "Offline";
    } else if (
      recentlyCommunicatedAttribute.currentValue === "True" &&
      (signalStrengthAttribute === undefined || signalStrengthAttribute.currentValue === null)
    ) {
      return "Excellent";
    } else if (
      recentlyCommunicatedAttribute.currentValue === "True" &&
      signalStrengthAttribute !== undefined &&
      signalStrengthAttribute.currentValue !== null
    ) {
      return signalStrengthAttribute.currentValue;
    }
    return "Unknown";
  }

  return (
    <span
      className={`${styles.signalIcon}`}
      title={signalStrength !== "Offline" ? `Signal Strength: ${signalStrength}` : "Device Is Offline"}
    >
      {signalStrength !== "Offline" ? (
        <img src={`/images/signal_icon_${signalStrength.toLocaleLowerCase()}.png`} alt="Signal Icon" />
      ) : (
        <img src={"/images/signal_icon_offline.png"} alt="Signal Icon" />
      )}
    </span>
  );
}

interface Props {
  allAttributes: Attribute[];
}

interface Attribute {
  regAttributeId: number;
  attributeCode: string;
  attributeName: string;
  connectedRegAttributeId: number | null;
  unitShortName: string | null;
  unitLongName: string | null;
  icon: string;
  currentValue: string | null;
  currentValueUtc: string | null;
  isHistorical: boolean;
  isGraphable: boolean;
  isStale: boolean;
}
